/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import { OperationStatus } from "Constants/OperationStatus";
import { defaultListingPageSize, defaultRdFilterValues } from "Constants/ProfileConstants";
import { removeDateTimeOffset } from "Utility/FormatHelper";

import { IRDUserProfileState } from "./IRDUserProfileState";
import {
  IRDAddUserProfileComment,
  IRDAddUserProfileCommentFail,
  IRDAddUserProfileCommentSuccess,
  IRDClaimBenefit,
  IRDClaimBenefitFail,
  IRDClaimBenefitSuccess,
  IRDDownloadToCSVFail,
  IRDDownloadToCSVSuccess,
  IRDExtendAzureBenefitCreditFailure,
  IRDFetchM365BenefitDetailFailure,
  IRDFetchM365BenefitDetailSuccess,
  IRDFetchProgramProfileFail,
  IRDFetchProgramProfileSuccess,
  IRDFetchUserProfile,
  IRDFetchUserProfileComments,
  IRDFetchUserProfileCommentsFail,
  IRDFetchUserProfileCommentsSuccess,
  IRDFetchUserProfileFail,
  IRDFetchUserProfileSuccess,
  IRDGetProfileBenefitsFail,
  IRDGetProfileBenefitsSuccess,
  IRDProfilesFail,
  IRDProfilesSearch,
  IRDProfilesSuccess,
  IRDUpdateCurrentPage,
  IRDUpdateM365BenefitDetailFailure,
  IRDUpdateM365BenefitDetailSuccess,
  IRDUpdatePageSize,
  IRDUpdateProgramProfile,
  IRDUpdateProgramProfileFail,
  IRDUpdateProgramProfileSuccess,
  IRDUpdateSorting,
  IRDUpdateUserProfileId,
  IRDUserProfilesFilterValueAdd,
  IRDUserProfilesFilterValueRemove,
  IRDUserProfilesFilterValueUpdate,
  IRDUserProfileIsPrivateUpdateFail,
  IRDUserProfilePrimaryEmailUpdateFail,
  RDUserProfileActionTypes,
  IRDAddAzureBenefitDetailFail,
  IRDFetchMS365BenefitDetailFailure,
  IRDFetchMS365BenefitDetailSuccess,
  IRDUpdateMS365BenefitDetailFailure,
  IRDUpdateMS365BenefitDetailSuccess,
} from "./RDUserProfileAction";
import {
  RD_ADD_AZURE_BENEFIT_DETAIL,
  RD_ADD_AZURE_BENEFIT_DETAIL_FAIL,
  RD_ADD_AZURE_BENEFIT_DETAIL_SUCCESS,
  RD_ADD_AZURE_BENEFIT_RESET,
  RD_ADD_USER_PROFILE_COMMENT,
  RD_ADD_USER_PROFILE_COMMENT_FAIL,
  RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES,
  RD_ADD_USER_PROFILE_COMMENT_SUCCESS,
  RD_DOWNLOAD_TO_CSV,
  RD_DOWNLOAD_TO_CSV_FAIL,
  RD_DOWNLOAD_TO_CSV_SUCCESS,
  RD_EXTEND_AZURE_BENEFIT_CREDIT,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_RESET,
  RD_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS,
  RD_FETCH_PROGRAM_PROFILE,
  RD_FETCH_PROGRAM_PROFILE_FAIL,
  RD_FETCH_PROGRAM_PROFILE_SUCCESS,
  RD_FETCH_USER_PROFILE,
  RD_FETCH_USER_PROFILE_COMMENTS,
  RD_FETCH_USER_PROFILE_COMMENTS_FAIL,
  RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS,
  RD_FETCH_USER_PROFILE_FAIL,
  RD_FETCH_USER_PROFILE_SUCCESS,
  RD_GET_M365_BENEFIT_DETAIL,
  RD_GET_M365_BENEFIT_DETAIL_FAILURE,
  RD_GET_M365_BENEFIT_DETAIL_SUCCESS,
  RD_GET_PROFILE_BENEFITS,
  RD_GET_PROFILE_BENEFITS_FAIL,
  RD_GET_PROFILE_BENEFITS_SUCCESS,
  RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS,
  RD_RESET_FILE_DOWNLOAD_STATUS,
  RD_RESET_M365_BENEFIT_DETAIL,
  RD_UPDATE_LISTING_CURRENT_PAGE,
  RD_UPDATE_LISTING_PAGE_SIZE,
  RD_UPDATE_LISTING_SORTING,
  RD_UPDATE_M365_BENEFIT_DETAIL,
  RD_UPDATE_M365_BENEFIT_DETAIL_FAILURE,
  RD_UPDATE_M365_BENEFIT_DETAIL_SUCCESS,
  RD_UPDATE_PROGRAM_PROFILE,
  RD_UPDATE_PROGRAM_PROFILE_FAIL,
  RD_UPDATE_PROGRAM_PROFILE_SUCCESS,
  RD_UPDATE_USER_PROFILE_ID,
  RD_USER_PROFILE_CLAIM_BENEFIT,
  RD_USER_PROFILE_CLAIM_BENEFIT_FAIL,
  RD_USER_PROFILE_CLAIM_BENEFIT_RESET,
  RD_USER_PROFILE_CLAIM_BENEFIT_SUCCESS,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET,
  RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS,
  RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE,
  RD_USER_PROFILES_FILTER_VALUE_ADD,
  RD_USER_PROFILES_FILTER_VALUE_REMOVE,
  RD_USER_PROFILES_FILTER_VALUE_RESET,
  RD_USER_PROFILES_FILTER_VALUE_UPDATE,
  RD_USER_PROFILES_SEARCH,
  RD_USER_PROFILES_SEARCH_FAIL,
  RD_USER_PROFILES_SEARCH_SUCCESS,
  RD_USERPROFILE_UPDATE_IS_PRIVATE,
  RD_USERPROFILE_UPDATE_IS_PRIVATE_FAIL,
  RD_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS,
  RD_RESET_MS365_BENEFIT_DETAIL,
  RD_GET_MS365_BENEFIT_DETAIL,
  RD_GET_MS365_BENEFIT_DETAIL_FAILURE,
  RD_GET_MS365_BENEFIT_DETAIL_SUCCESS,
  RD_UPDATE_MS365_BENEFIT_DETAIL,
  RD_UPDATE_MS365_BENEFIT_DETAIL_FAILURE,
  RD_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS,
} from "./RDUserProfileActionTypes";

const INITIAL_STATE: IRDUserProfileState = {
  userProfiles: [],
  userProfileFilterValues: defaultRdFilterValues,
  error: "",
  isUserProfilesLoading: true,
  isSelectedUserProfileUpdating: false,
  updateUserProfileError: "",
  isCurrentUserProfileLoading: true,
  formSubmittingComplete: false,
  fetchUserProfileCommentsStatus: OperationStatus.Idle,
  successMessage: "",
  userProfileComments: [],
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
  programProfileUpdateError: "",
  filteredCount: 0,
  fileDownloadStatus: OperationStatus.Idle,
  updateProgramProfileOperationStatus: OperationStatus.Idle,
  fetchUserProfileOperationStatus: OperationStatus.Idle,
  addUserProfileCommentOperationStatus: OperationStatus.Idle,
  fetchProgramProfileErrorMessage: "",
  selectedUserProfileId: 0,
  isClaimingBenefit: false,
  isClaimingBenefitStatus: OperationStatus.Idle,
  claimBenefitError: "",
  profileBenefits: {
    azure: [],
    githubPro: [],
    mcp: [],
    linkedIn: [],
    visualStudio: [],
    m365: [],
    benefitTenant: {},
    teams: [],
  },
  profileBenefitsErrorMessage: "",
  profileBenefitsStatus: OperationStatus.Idle,
  m365BenefitDetailState: {
    m365BenefitDetail: {
      tenantId: "",
      tenantName: "",
      userProfileId: 0,
    },

    m365FetchErrorMessage: "",
    m365UpdateErrorMessage: "",
    m365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  },
  ms365BenefitDetailState: {
    ms365FetchErrorMessage: "",
    ms365UpdateErrorMessage: "",
    ms365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
    ms365BenefitDetail: {
      subscriptionId: "",
      tenantId: "",
      tenantName: "",
      userProfileId: 0,
    },
  },
  teamsBenefitDetailState: {
    teamsBenefitDetail: {
      userProfileId: 0,
      expirationDate: "",
      claimDate: "",
      code: "",
    },

    teamsFetchErrorMessage: "",
    teamsUpdateErrorMessage: "",
    teamsOperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  },
  azureBenefitExtendCreditState: {
    extendAzureCreditOperationStatus: { update: OperationStatus.Idle },
    extendAzureCreditError: "",
  },
  azureBenefitAddState: {
    addAzureBenefitOperationStatus: { update: OperationStatus.Idle },
    addAzureBenefitError: "",
  },
  programProfileOperationStatus: {
    read: OperationStatus.Idle,
  },
  selectedUserProfile: {
    address1: "",
    address2: "",
    addressCity: "",
    addressCountryOrRegionName: "",
    addressPostCode: "",
    addressStateOrProvince: "",
    applicationFormDateLastModified: "",
    applicationFormDateSubmitted: "",
    applicationStatus: "",
    awardNameLocalized: false,
    biography: "",
    companyTypeName: "",
    currentEmployerName: "",
    dateOfBirth: undefined,
    firstName: "",
    genderName: "",
    genderPronounPreferenceName: "",
    headline: "",
    id: 0,
    industryFocusNames: [],
    jobTitle: "",
    languages: [],
    lastName: "",
    localizedFirstName: "",
    localizedLastName: "",
    microsoftReferralId: "",
    primaryEmail: "",
    primaryPhone: "",
    primaryPhoneCountryOrRegionId: 0,
    primaryPhoneCountryOrRegionName: "",
    programProfile: undefined,
    programProfileStatus: "",
    screenNameLocalized: false,
    secondaryEmail: "",
    shippingAddress1: "",
    shippingAddress2: "",
    shippingCity: "",
    shippingCountryOrRegionName: "",
    shippingName: "",
    shippingPhoneCountryOrRegionId: 0,
    shippingPhoneCountryOrRegionName: "",
    shippingPhoneNumber: "",
    shippingPostCode: "",
    shippingSameAsAddress: false,
    shippingStateOrProvince: "",
    shirtFitType: "",
    shirtSize: "",
    technicalExpertiseNames: [],
    tenants: [],
    titleName: "",
    userProfileSocialNetwork: [],
    userProfileProgramLanguage: [],
    userProfileFunctionalRole: [],
    cpm: "",
  },
  updateIsPrivateProfileError: "",
  updateIsPrivateProfileStatus: OperationStatus.Idle,
  updatePrimaryEmailError: "",
  updatePrimaryEmailStatus: OperationStatus.Idle,
};

const applyUpdateCurrentPage = (
  state: IRDUserProfileState,
  action: IRDUpdateCurrentPage
): IRDUserProfileState => ({
  ...state,
  currentPage: action.currentPage,
});

const applyUpdatePageSize = (
  state: IRDUserProfileState,
  action: IRDUpdatePageSize
): IRDUserProfileState => ({
  ...state,
  pageSize: action.pageSize,
});

const applyUpdateSorting = (
  state: IRDUserProfileState,
  action: IRDUpdateSorting
): IRDUserProfileState => ({
  ...state,
  currentPage: state.currentPage > 1 ? 1 : state.currentPage,
  sortColumn: action.sortColumn,
  sortDescending: action.sortDescending,
});

const applyFileDownloadStatus = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Idle,
});

const applySearchUserProfiles = (
  state: IRDUserProfileState,
  action: IRDProfilesSearch
): IRDUserProfileState => ({
  ...state,
  isUserProfilesLoading: true,
  searchText: action.searchText,
  currentPage: action.searchPage,
  pageSize: action.pageSize,
  sortColumn: action.sortColumn,
  sortDescending: action.sortDescending,
});

const applySearchUserProfilesSuccess = (
  state: IRDUserProfileState,
  action: IRDProfilesSuccess
): IRDUserProfileState => ({
  ...state,
  userProfiles: action.userProfiles,
  currentPage: action.searchPage,
  pageSize: action.pageSize,
  isUserProfilesLoading: false,
  filteredCount: action.filteredCount,
});

const applySearchUserProfilesFail = (
  state: IRDUserProfileState,
  action: IRDProfilesFail
): IRDUserProfileState => ({
  ...state,
  error: action.error,
  isUserProfilesLoading: false,
});

const applyDownloadToCSV = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  fileDownloadErrorMessage: undefined,
  fileDownloadStatus: OperationStatus.Pending,
});

const applyDownloadToCSVSuccess = (
  state: IRDUserProfileState,
  action: IRDDownloadToCSVSuccess
): IRDUserProfileState => ({
  ...state,
  fileDownloadErrorMessage: undefined,
  fileDownloadStatus: OperationStatus.Succeeded,
  fileDownloadData: action.csvFile,
});

const applyDownloadToCSVFailed = (
  state: IRDUserProfileState,
  action: IRDDownloadToCSVFail
): IRDUserProfileState => ({
  ...state,
  fileDownloadStatus: OperationStatus.Failed,
  fileDownloadErrorMessage: action.errorMessage,
});

const addUserProfilesFilterValue = (
  state: IRDUserProfileState,
  action: IRDUserProfilesFilterValueAdd
): IRDUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.concat([action.filterValue]),
});

const removeUserProfilesFilterValue = (
  state: IRDUserProfileState,
  action: IRDUserProfilesFilterValueRemove
): IRDUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.filter(
    (item) =>
      !(item.fieldName === action.filterValue.fieldName && item.value === action.filterValue.value)
  ),
});

const removeUserProfilesFilterProvinceValue = (
  state: IRDUserProfileState
): IRDUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues.filter(
    (item) => !(item.fieldName === "stateOrProvince")
  ),
});

const updateUserProfilesFilterValue = (
  state: IRDUserProfileState,
  action: IRDUserProfilesFilterValueUpdate
): IRDUserProfileState => ({
  ...state,
  userProfileFilterValues: state.userProfileFilterValues
    .filter((item) => !(item.fieldName === action.filterValue.fieldName))
    .concat([action.filterValue]),
});

const applyFetchUserProfileComments = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfileComments
): IRDUserProfileState => ({
  ...state,
  fetchUserProfileCommentsErrorMessage: undefined,
  userProfileComments: [],
  fetchUserProfileCommentsStatus: OperationStatus.Pending,
});

const applyFetchUserProfileCommentsSuccess = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfileCommentsSuccess
): IRDUserProfileState => ({
  ...state,
  userProfileComments: action.userProfileComments,
  fetchUserProfileCommentsErrorMessage: undefined,
  fetchUserProfileCommentsStatus: OperationStatus.Succeeded,
});

const applyFetchUserProfileCommentsFail = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfileCommentsFail
): IRDUserProfileState => ({
  ...state,
  fetchUserProfileCommentsErrorMessage: action.errorMessage,
  fetchUserProfileCommentsStatus: OperationStatus.Failed,
});

const applyFetchUserProfile = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfile
): IRDUserProfileState => ({
  ...state,
  selectedUserProfileId: action.rdUserProfileId,
  selectedUserProfile: {
    ...INITIAL_STATE.selectedUserProfile,
  },
  fetchUserProfileErrorMessage: undefined,
  fetchUserProfileOperationStatus: OperationStatus.Pending,
});

const applyFetchUserProfileSuccess = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfileSuccess
): IRDUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...action.rdUserProfile,
    dateOfBirth: removeDateTimeOffset(action.rdUserProfile.dateOfBirth),
  },
  fetchUserProfileErrorMessage: undefined,
  fetchUserProfileOperationStatus: OperationStatus.Succeeded,
});

const applyFetchUserProfileFail = (
  state: IRDUserProfileState,
  action: IRDFetchUserProfileFail
): IRDUserProfileState => ({
  ...state,
  fetchUserProfileErrorMessage: action.errorMessage,
  fetchUserProfileOperationStatus: OperationStatus.Failed,
});

const applyAddUserProfileComment = (
  state: IRDUserProfileState,
  action: IRDAddUserProfileComment
): IRDUserProfileState => ({
  ...state,
  addUserProfileCommentErrorMessage: undefined,
  addUserProfileCommentOperationStatus: OperationStatus.Pending,
});

const applyAddUserProfileCommentSuccess = (
  state: IRDUserProfileState,
  action: IRDAddUserProfileCommentSuccess
): IRDUserProfileState => ({
  ...state,
  addUserProfileCommentErrorMessage: undefined,
  addUserProfileCommentOperationStatus: OperationStatus.Succeeded,
});

const applyAddUserProfileCommentFail = (
  state: IRDUserProfileState,
  action: IRDAddUserProfileCommentFail
): IRDUserProfileState => ({
  ...state,
  addUserProfileCommentErrorMessage: action.errorMessage,
  addUserProfileCommentOperationStatus: OperationStatus.Failed,
});

const applyAddUserProfileCommentResetValues = (
  state: IRDUserProfileState
): IRDUserProfileState => ({
  ...state,
  addUserProfileCommentErrorMessage: undefined,
  addUserProfileCommentOperationStatus: OperationStatus.Idle,
  fetchUserProfileCommentsErrorMessage: undefined,
  fetchUserProfileCommentsStatus: OperationStatus.Idle,
});

const applyUpdateUserProfileId = (
  state: IRDUserProfileState,
  action: IRDUpdateUserProfileId
): IRDUserProfileState => ({
  ...state,
  selectedUserProfileId: action.selectedUserProfileId,
});

const applyUpdateProgramProfile = (
  state: IRDUserProfileState,
  action: IRDUpdateProgramProfile
): IRDUserProfileState => ({
  ...state,
  updateProgramProfileOperationStatus: OperationStatus.Pending,
  updateProgramProfileErrorMessage: undefined,
});

const applyUpdateProgramProfileSuccess = (
  state: IRDUserProfileState,
  action: IRDUpdateProgramProfileSuccess
): IRDUserProfileState => {
  if (!!state.selectedUserProfile) {
    return {
      ...state,
      selectedUserProfile: {
        ...state.selectedUserProfile,
        programProfileStatus: action.savedProgramProfileStatusName,
      },
      updateProgramProfileErrorMessage: undefined,
      updateProgramProfileOperationStatus: OperationStatus.Succeeded,
    };
  }
  return state;
};

const applyUpdateProgramProfileFail = (
  state: IRDUserProfileState,
  action: IRDUpdateProgramProfileFail
): IRDUserProfileState => ({
  ...state,
  updateProgramProfileErrorMessage: action.errorMessage,
  updateProgramProfileOperationStatus: OperationStatus.Failed,
});

const applyUpdateProgramProfileResetStatus = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  updateProgramProfileErrorMessage: undefined,
  updateProgramProfileOperationStatus: OperationStatus.Idle,
});

const applyClaimingRdBenefit = (
  state: IRDUserProfileState,
  action: IRDClaimBenefit
): IRDUserProfileState => ({
  ...state,
  isClaimingBenefit: true,
  isClaimingBenefitStatus: OperationStatus.Pending,
  claimBenefitError: "",
});

const applyClaimingRdBenefitSuccess = (
  state: IRDUserProfileState,
  action: IRDClaimBenefitSuccess
): IRDUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitStatus: OperationStatus.Succeeded,
});

const applyClaimingRdBenefitFail = (
  state: IRDUserProfileState,
  action: IRDClaimBenefitFail
): IRDUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitStatus: OperationStatus.Failed,
  claimBenefitError: action.error,
});

const applyClaimingRdBenefitReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  isClaimingBenefit: false,
  isClaimingBenefitStatus: OperationStatus.Idle,
  claimBenefitError: "",
});

const applyGetProfileBenefits = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  profileBenefitsStatus: OperationStatus.Pending,
  profileBenefitsErrorMessage: "",
});

const applyGetProfileBenefitsSuccess = (
  state: IRDUserProfileState,
  action: IRDGetProfileBenefitsSuccess
): IRDUserProfileState => ({
  ...state,
  profileBenefits: action.userProfileBenefits,
  profileBenefitsStatus: OperationStatus.Succeeded,
});

const applyProfileBenefitsFail = (
  state: IRDUserProfileState,
  action: IRDGetProfileBenefitsFail
): IRDUserProfileState => ({
  ...state,
  profileBenefitsErrorMessage: action.profileBenefitsErrorMessage,
  profileBenefitsStatus: OperationStatus.Failed,
});

const applyExtendAzureBenefitCredit = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Pending },
    extendAzureCreditError: "",
  },
});

const applyExtendAzureBenefitCreditSuccess = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Succeeded },
  },
});

const applyExtendAzureBenefitCreditFail = (
  state: IRDUserProfileState,
  action: IRDExtendAzureBenefitCreditFailure
): IRDUserProfileState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Failed },
    extendAzureCreditError: action.error,
  },
});

const applyExtendAzureBenefitCreditReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitExtendCreditState: {
    ...state.azureBenefitExtendCreditState,
    extendAzureCreditOperationStatus: { update: OperationStatus.Idle },
    extendAzureCreditError: "",
  },
});

const applyAddAzureBenefit = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Pending },
    addAzureBenefitError: "",
  },
});

const applyAddAzureBenefitSuccess = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Succeeded },
  },
});

const applyAddAzureBenefitFail = (
  state: IRDUserProfileState,
  action: IRDAddAzureBenefitDetailFail
): IRDUserProfileState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Failed },
    addAzureBenefitError: action.errorMessage,
  },
});

const applyAddAzureBenefitReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  azureBenefitAddState: {
    ...state.azureBenefitAddState,
    addAzureBenefitOperationStatus: { update: OperationStatus.Idle },
    addAzureBenefitError: "",
  },
});

const applyM365BenefitDetailReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365OperationStatus: {
      read: OperationStatus.Idle,
      update: OperationStatus.Idle,
    },
  },
});

const applyGetM365BenefitDetailsSuccess = (
  state: IRDUserProfileState,
  action: IRDFetchM365BenefitDetailSuccess
): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365BenefitDetail: action.m365BenefitDetail,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetM365BenefitDetailsFailure = (
  state: IRDUserProfileState,
  action: IRDFetchM365BenefitDetailFailure
): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365FetchErrorMessage: action.m365FetchErrorMessage,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetM365BenefitDetails = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365FetchErrorMessage: "",
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyUpdateM365BenefitDetails = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365UpdateErrorMessage: "",
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Pending,
    },
  },
});

const applyUpdateM365BenefitDetailsSuccess = (
  state: IRDUserProfileState,
  action: IRDUpdateM365BenefitDetailSuccess
): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365BenefitDetail: action.m365BenefitDetail,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Succeeded,
    },
  },
});

const applyUpdateM365BenefitDetailsFailure = (
  state: IRDUserProfileState,
  action: IRDUpdateM365BenefitDetailFailure
): IRDUserProfileState => ({
  ...state,
  m365BenefitDetailState: {
    ...state.m365BenefitDetailState,
    m365UpdateErrorMessage: action.m365UpdateErrorMessage,
    m365OperationStatus: {
      ...state.m365BenefitDetailState.m365OperationStatus,
      update: OperationStatus.Failed,
    },
  },
});

const applyMS365BenefitDetailReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365OperationStatus: { read: OperationStatus.Idle, update: OperationStatus.Idle },
  },
});

const applyGetMS365BenefitDetailsSuccess = (
  state: IRDUserProfileState,
  action: IRDFetchMS365BenefitDetailSuccess
): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365BenefitDetail: action.ms365BenefitDetail,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Succeeded,
    },
  },
});

const applyGetMS365BenefitDetailsFailure = (
  state: IRDUserProfileState,
  action: IRDFetchMS365BenefitDetailFailure
): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: action.ms365FetchErrorMessage,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Failed,
    },
  },
});

const applyGetMS365BenefitDetails = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: "",
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      read: OperationStatus.Pending,
    },
  },
});

const applyUpdateMS365BenefitDetails = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365UpdateErrorMessage: "",
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Pending,
    },
  },
});

const applyUpdateMS365BenefitDetailsSuccess = (
  state: IRDUserProfileState,
  action: IRDUpdateMS365BenefitDetailSuccess
): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365BenefitDetail: action.ms365BenefitDetail,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Succeeded,
    },
  },
});

const applyUpdateMS365BenefitDetailsFailure = (
  state: IRDUserProfileState,
  action: IRDUpdateMS365BenefitDetailFailure
): IRDUserProfileState => ({
  ...state,
  ms365BenefitDetailState: {
    ...state.ms365BenefitDetailState,
    ms365FetchErrorMessage: action.ms365UpdateErrorMessage,
    ms365OperationStatus: {
      ...state.ms365BenefitDetailState.ms365OperationStatus,
      update: OperationStatus.Failed,
    },
  },
});

const applyFetchProgramProfile = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: undefined,
  },
  programProfileOperationStatus: { read: OperationStatus.Pending },
  updateProgramProfileOperationStatus: OperationStatus.Idle,
  fetchProgramProfileErrorMessage: "",
});

const applyFetchProgramProfileSuccess = (
  state: IRDUserProfileState,
  action: IRDFetchProgramProfileSuccess
): IRDUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: action.programProfile,
  },
  programProfileOperationStatus: { read: OperationStatus.Succeeded },
});

const applyFetchProgramProfileFail = (
  state: IRDUserProfileState,
  action: IRDFetchProgramProfileFail
): IRDUserProfileState => ({
  ...state,
  selectedUserProfile: {
    ...state.selectedUserProfile,
    programProfile: undefined,
  },
  programProfileOperationStatus: { read: OperationStatus.Failed },
  fetchProgramProfileErrorMessage: action.errorMessage,
});

const applyUserProfileIsPrivateUpdate = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Pending,
  updateIsPrivateProfileError: "",
});

const applyUserProfileIsPrivateUpdateSuccess = (
  state: IRDUserProfileState
): IRDUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Succeeded,
});

const applyUserProfileIsPrivateUpdateFail = (
  state: IRDUserProfileState,
  action: IRDUserProfileIsPrivateUpdateFail
): IRDUserProfileState => ({
  ...state,
  updateIsPrivateProfileStatus: OperationStatus.Failed,
  updateIsPrivateProfileError: action.error,
});

const applyUserProfileFilterValueReset = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  userProfiles: [],
  userProfileFilterValues: defaultRdFilterValues,
  currentPage: 1,
  pageSize: defaultListingPageSize,
  searchText: "",
});

const applyUserProfilePrimaryEmailUpdate = (state: IRDUserProfileState): IRDUserProfileState => ({
  ...state,
  updatePrimaryEmailStatus: OperationStatus.Pending,
  updatePrimaryEmailError: "",
});

const applyUserProfilePrimaryEmailUpdateSuccess = (
  state: IRDUserProfileState
): IRDUserProfileState => ({
  ...state,
  updatePrimaryEmailStatus: OperationStatus.Succeeded,
});

const applyUserProfilePrimaryEmailUpdateFail = (
  state: IRDUserProfileState,
  action: IRDUserProfilePrimaryEmailUpdateFail
): IRDUserProfileState => ({
  ...state,
  updatePrimaryEmailStatus: OperationStatus.Failed,
  updatePrimaryEmailError: action.error,
});

const applyUserProfilePrimaryEmailUpdateReset = (
  state: IRDUserProfileState
): IRDUserProfileState => ({
  ...state,
  updatePrimaryEmailStatus: OperationStatus.Idle,
  updatePrimaryEmailError: "",
});

function rdUserProfileReducer(
  state = INITIAL_STATE,
  action: RDUserProfileActionTypes
): IRDUserProfileState {
  switch (action.type) {
    case RD_RESET_M365_BENEFIT_DETAIL:
      return applyM365BenefitDetailReset(state);
    case RD_GET_M365_BENEFIT_DETAIL:
      return applyGetM365BenefitDetails(state);
    case RD_GET_M365_BENEFIT_DETAIL_FAILURE:
      return applyGetM365BenefitDetailsFailure(state, action);
    case RD_GET_M365_BENEFIT_DETAIL_SUCCESS:
      return applyGetM365BenefitDetailsSuccess(state, action);
    case RD_UPDATE_M365_BENEFIT_DETAIL:
      return applyUpdateM365BenefitDetails(state);
    case RD_UPDATE_M365_BENEFIT_DETAIL_SUCCESS:
      return applyUpdateM365BenefitDetailsSuccess(state, action);
    case RD_UPDATE_M365_BENEFIT_DETAIL_FAILURE:
      return applyUpdateM365BenefitDetailsFailure(state, action);
    case RD_RESET_MS365_BENEFIT_DETAIL:
      return applyMS365BenefitDetailReset(state);
    case RD_GET_MS365_BENEFIT_DETAIL:
      return applyGetMS365BenefitDetails(state);
    case RD_GET_MS365_BENEFIT_DETAIL_FAILURE:
      return applyGetMS365BenefitDetailsFailure(state, action);
    case RD_GET_MS365_BENEFIT_DETAIL_SUCCESS:
      return applyGetMS365BenefitDetailsSuccess(state, action);
    case RD_UPDATE_MS365_BENEFIT_DETAIL:
      return applyUpdateMS365BenefitDetails(state);
    case RD_UPDATE_MS365_BENEFIT_DETAIL_SUCCESS:
      return applyUpdateMS365BenefitDetailsSuccess(state, action);
    case RD_UPDATE_MS365_BENEFIT_DETAIL_FAILURE:
      return applyUpdateMS365BenefitDetailsFailure(state, action);
    case RD_EXTEND_AZURE_BENEFIT_CREDIT:
      return applyExtendAzureBenefitCredit(state);
    case RD_EXTEND_AZURE_BENEFIT_CREDIT_SUCCESS:
      return applyExtendAzureBenefitCreditSuccess(state);
    case RD_EXTEND_AZURE_BENEFIT_CREDIT_FAILURE:
      return applyExtendAzureBenefitCreditFail(state, action);
    case RD_EXTEND_AZURE_BENEFIT_CREDIT_RESET:
      return applyExtendAzureBenefitCreditReset(state);
    case RD_GET_PROFILE_BENEFITS: {
      return applyGetProfileBenefits(state);
    }
    case RD_GET_PROFILE_BENEFITS_FAIL: {
      return applyProfileBenefitsFail(state, action);
    }
    case RD_GET_PROFILE_BENEFITS_SUCCESS: {
      return applyGetProfileBenefitsSuccess(state, action);
    }
    case RD_UPDATE_LISTING_CURRENT_PAGE: {
      return applyUpdateCurrentPage(state, action);
    }
    case RD_UPDATE_LISTING_PAGE_SIZE: {
      return applyUpdatePageSize(state, action);
    }
    case RD_UPDATE_LISTING_SORTING: {
      return applyUpdateSorting(state, action);
    }
    case RD_RESET_FILE_DOWNLOAD_STATUS: {
      return applyFileDownloadStatus(state);
    }
    case RD_USER_PROFILES_SEARCH: {
      return applySearchUserProfiles(state, action);
    }
    case RD_USER_PROFILES_SEARCH_SUCCESS: {
      return applySearchUserProfilesSuccess(state, action);
    }
    case RD_USER_PROFILES_SEARCH_FAIL: {
      return applySearchUserProfilesFail(state, action);
    }
    case RD_USER_PROFILES_FILTER_VALUE_ADD: {
      return addUserProfilesFilterValue(state, action);
    }
    case RD_USER_PROFILES_FILTER_VALUE_REMOVE: {
      return removeUserProfilesFilterValue(state, action);
    }
    case RD_USER_PROFILES_FILTER_PROVINCE_VALUE_REMOVE: {
      return removeUserProfilesFilterProvinceValue(state);
    }
    case RD_USER_PROFILES_FILTER_VALUE_UPDATE: {
      return updateUserProfilesFilterValue(state, action);
    }
    case RD_DOWNLOAD_TO_CSV: {
      return applyDownloadToCSV(state);
    }
    case RD_DOWNLOAD_TO_CSV_SUCCESS: {
      return applyDownloadToCSVSuccess(state, action);
    }
    case RD_DOWNLOAD_TO_CSV_FAIL: {
      return applyDownloadToCSVFailed(state, action);
    }
    case RD_FETCH_USER_PROFILE: {
      return applyFetchUserProfile(state, action);
    }
    case RD_FETCH_USER_PROFILE_SUCCESS: {
      return applyFetchUserProfileSuccess(state, action);
    }
    case RD_FETCH_USER_PROFILE_FAIL: {
      return applyFetchUserProfileFail(state, action);
    }
    case RD_UPDATE_USER_PROFILE_ID: {
      return applyUpdateUserProfileId(state, action);
    }
    case RD_FETCH_USER_PROFILE_COMMENTS: {
      return applyFetchUserProfileComments(state, action);
    }
    case RD_FETCH_USER_PROFILE_COMMENTS_SUCCESS: {
      return applyFetchUserProfileCommentsSuccess(state, action);
    }
    case RD_FETCH_USER_PROFILE_COMMENTS_FAIL: {
      return applyFetchUserProfileCommentsFail(state, action);
    }
    case RD_ADD_AZURE_BENEFIT_DETAIL:
      return applyAddAzureBenefit(state);
    case RD_ADD_AZURE_BENEFIT_DETAIL_SUCCESS:
      return applyAddAzureBenefitSuccess(state);
    case RD_ADD_AZURE_BENEFIT_DETAIL_FAIL:
      return applyAddAzureBenefitFail(state, action);
    case RD_ADD_AZURE_BENEFIT_RESET:
      return applyAddAzureBenefitReset(state);
    case RD_ADD_USER_PROFILE_COMMENT: {
      return applyAddUserProfileComment(state, action);
    }
    case RD_ADD_USER_PROFILE_COMMENT_SUCCESS: {
      return applyAddUserProfileCommentSuccess(state, action);
    }
    case RD_ADD_USER_PROFILE_COMMENT_FAIL: {
      return applyAddUserProfileCommentFail(state, action);
    }
    case RD_ADD_USER_PROFILE_COMMENT_RESET_VALUES: {
      return applyAddUserProfileCommentResetValues(state);
    }
    case RD_UPDATE_PROGRAM_PROFILE: {
      return applyUpdateProgramProfile(state, action);
    }
    case RD_UPDATE_PROGRAM_PROFILE_FAIL: {
      return applyUpdateProgramProfileFail(state, action);
    }
    case RD_UPDATE_PROGRAM_PROFILE_SUCCESS: {
      return applyUpdateProgramProfileSuccess(state, action);
    }
    case RD_PROGRAM_PROFILE_UPDATE_RESET_STATUS: {
      return applyUpdateProgramProfileResetStatus(state);
    }
    case RD_USER_PROFILE_CLAIM_BENEFIT: {
      return applyClaimingRdBenefit(state, action);
    }
    case RD_USER_PROFILE_CLAIM_BENEFIT_SUCCESS: {
      return applyClaimingRdBenefitSuccess(state, action);
    }
    case RD_USER_PROFILE_CLAIM_BENEFIT_FAIL: {
      return applyClaimingRdBenefitFail(state, action);
    }
    case RD_USER_PROFILE_CLAIM_BENEFIT_RESET: {
      return applyClaimingRdBenefitReset(state);
    }
    case RD_FETCH_PROGRAM_PROFILE: {
      return applyFetchProgramProfile(state);
    }
    case RD_FETCH_PROGRAM_PROFILE_SUCCESS: {
      return applyFetchProgramProfileSuccess(state, action);
    }
    case RD_FETCH_PROGRAM_PROFILE_FAIL: {
      return applyFetchProgramProfileFail(state, action);
    }
    case RD_USERPROFILE_UPDATE_IS_PRIVATE: {
      return applyUserProfileIsPrivateUpdate(state);
    }
    case RD_USERPROFILE_UPDATE_IS_PRIVATE_SUCCESS: {
      return applyUserProfileIsPrivateUpdateSuccess(state);
    }
    case RD_USERPROFILE_UPDATE_IS_PRIVATE_FAIL: {
      return applyUserProfileIsPrivateUpdateFail(state, action);
    }
    case RD_USER_PROFILES_FILTER_VALUE_RESET: {
      return applyUserProfileFilterValueReset(state);
    }
    case RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL: {
      return applyUserProfilePrimaryEmailUpdate(state);
    }
    case RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_SUCCESS: {
      return applyUserProfilePrimaryEmailUpdateSuccess(state);
    }
    case RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_FAIL: {
      return applyUserProfilePrimaryEmailUpdateFail(state, action);
    }
    case RD_USER_PROFILE_UPDATE_PRIMARY_EMAIL_RESET: {
      return applyUserProfilePrimaryEmailUpdateReset(state);
    }
    default:
      return state;
  }
}

export default rdUserProfileReducer;
